import { useQuery } from '@tanstack/vue-query';
import { computed } from 'vue';
import axiosInstance from '@/services/interceptor.services';
import { EquipmentService } from '@/services/equipment.services';
import type { IDeviceType } from '@/types/equipment.types';

export interface IStoreOption {
  id: string;
  name: string;
}

const FIVE_MINUTES = 5 * 60_000;

// Les commerces proposés dans les filtres et les formulaires.
export const useStoreOptions = () => {
  const { data } = useQuery({
    queryKey: ['equipment', 'stores'],
    queryFn: async () =>
      (
        await axiosInstance.get(
          'restaurants/search?pagination=false&sortBy=name&order=ASC',
        )
      ).data,
    staleTime: FIVE_MINUTES,
  });
  const stores = computed<IStoreOption[]>(() => {
    const list = data.value?.data ?? data.value?.restaurants ?? [];
    return Array.isArray(list)
      ? list.map((s: { id: string; designation?: string; name?: string }) => ({
          id: s.id,
          name: s.designation ?? s.name ?? s.id,
        }))
      : [];
  });
  const storeName = (id: string) =>
    stores.value.find((s) => s.id === id)?.name ?? id;
  return { stores, storeName };
};

// Le catalogue des types : ce que chaque type attend (MAC, port, réglages, rôles).
export const useDeviceTypes = () => {
  const { data } = useQuery({
    queryKey: ['equipment', 'device-types'],
    queryFn: EquipmentService.deviceTypes,
    staleTime: FIVE_MINUTES,
  });
  // Jamais autre chose qu'un tableau : un catalogue illisible vide le formulaire au lieu de le faire planter.
  const types = computed<IDeviceType[]>(() =>
    Array.isArray(data.value) ? data.value : [],
  );
  const typeOf = (type: string) => types.value.find((t) => t.type === type);
  const typeLabel = (type: string) => typeOf(type)?.label ?? type;
  return { types, typeOf, typeLabel };
};

// Le message du serveur (400, 404, 409) tel quel : il dit déjà ce qui est refusé.
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const message = (
    error as { response?: { data?: { message?: unknown } } } | undefined
  )?.response?.data?.message;
  if (typeof message === 'string') return message;
  if (Array.isArray(message)) return message.join(' · ');
  return fallback;
};
